import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import StickyHeader from "@/components/StickyHeader";

// Halaman master data modul Rotasi Klinik (khusus Super Admin) -- kelola
// Departemen, Divisi (terikat ke satu Departemen), dan Instalasi (flat list,
// lintas departemen). Dipakai sbg dropdown di tab "Rotasi Klinik" Edit Peserta.
export default function DataRotasiKlinik() {
    const [departemen, setDepartemen] = useState([]);
    const [divisi, setDivisi] = useState([]);
    const [instalasi, setInstalasi] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [d, v, i] = await Promise.all([
                api.get("/departemen-klinik"), api.get("/divisi-klinik"), api.get("/instalasi-klinik"),
            ]);
            setDepartemen(d.data); setDivisi(v.data); setInstalasi(i.data);
        } catch {
            toast.error("Gagal memuat data referensi Rotasi Klinik");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadAll(); }, []);

    const departemenMap = useMemo(() => {
        const m = {};
        departemen.forEach((d) => (m[d.id] = d));
        return m;
    }, [departemen]);
    const divisiCountByDep = useMemo(() => {
        const m = {};
        divisi.forEach((d) => { m[d.departemen_id] = (m[d.departemen_id] || 0) + 1; });
        return m;
    }, [divisi]);

    return (
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Master Data</div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                        Master Rotasi Klinik
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Daftar Departemen, Divisi, dan Instalasi yang dipakai di form Rotasi Klinik peserta didik.
                    </p>
                </div>
            </StickyHeader>

            {loading ? (
                <div className="p-10 text-slate-500">Memuat data...</div>
            ) : (
                <Tabs defaultValue="departemen" className="w-full">
                    <TabsList>
                        <TabsTrigger value="departemen" data-testid="rk-tab-departemen">Departemen ({departemen.length})</TabsTrigger>
                        <TabsTrigger value="divisi" data-testid="rk-tab-divisi">Divisi ({divisi.length})</TabsTrigger>
                        <TabsTrigger value="instalasi" data-testid="rk-tab-instalasi">Instalasi ({instalasi.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="departemen">
                        <DepartemenPanel
                            departemen={departemen} divisiCountByDep={divisiCountByDep} reload={loadAll}
                        />
                    </TabsContent>
                    <TabsContent value="divisi">
                        <DivisiPanel
                            divisi={divisi} departemen={departemen} departemenMap={departemenMap} reload={loadAll}
                        />
                    </TabsContent>
                    <TabsContent value="instalasi">
                        <InstalasiPanel instalasi={instalasi} reload={loadAll} />
                    </TabsContent>
                </Tabs>
            )}
        </div>
    );
}

function DepartemenPanel({ departemen, divisiCountByDep, reload }) {
    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [nama, setNama] = useState("");
    const [confirmDel, setConfirmDel] = useState(null);

    const openAdd = () => { setEditing(null); setNama(""); setDialog(true); };
    const openEdit = (d) => { setEditing(d); setNama(d.nama); setDialog(true); };

    const submit = async () => {
        try {
            if (editing) await api.put(`/departemen-klinik/${editing.id}`, { nama });
            else await api.post("/departemen-klinik", { nama });
            toast.success(editing ? "Departemen diperbarui" : "Departemen ditambahkan");
            setDialog(false); reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const del = async () => {
        try {
            await api.delete(`/departemen-klinik/${confirmDel.id}`);
            toast.success("Departemen dihapus");
            setConfirmDel(null); reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <>
            <div className="flex justify-end mb-3">
                <Button size="sm" onClick={openAdd} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="departemen-add-button">
                    <Plus className="w-4 h-4 mr-1.5" /> Tambah Departemen
                </Button>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full compact">
                    <thead className="bg-slate-100">
                        <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                            <th>Nama Departemen</th><th>Jumlah Divisi</th><th className="text-right pr-4">Aksi</th>
                        </tr>
                    </thead>
                    <tbody>
                        {departemen.map((d) => (
                            <tr key={d.id} className="border-t border-slate-200 hover:bg-slate-50">
                                <td className="font-semibold text-slate-800">{d.nama}</td>
                                <td><Badge variant="outline">{divisiCountByDep[d.id] || 0}</Badge></td>
                                <td className="text-right pr-2">
                                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)}><Pencil className="w-4 h-4" /></Button>
                                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setConfirmDel(d)}><Trash2 className="w-4 h-4" /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? "Edit Departemen" : "Tambah Departemen"}</DialogTitle></DialogHeader>
                    <div>
                        <Label className="text-xs font-semibold uppercase text-slate-600">Nama Departemen</Label>
                        <Input data-testid="departemen-form-nama" value={nama} onChange={(e) => setNama(e.target.value)} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Batal</Button>
                        <Button onClick={submit} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="departemen-form-submit">Simpan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus departemen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmDel?.nama} akan dihapus. Departemen dengan Divisi atau data Rotasi Klinik terkait tidak bisa dihapus.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={del}>Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

function DivisiPanel({ divisi, departemen, departemenMap, reload }) {
    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ nama: "", departemen_id: "" });
    const [confirmDel, setConfirmDel] = useState(null);
    const [filterDep, setFilterDep] = useState("all");

    const openAdd = () => { setEditing(null); setForm({ nama: "", departemen_id: departemen[0]?.id || "" }); setDialog(true); };
    const openEdit = (d) => { setEditing(d); setForm({ nama: d.nama, departemen_id: d.departemen_id }); setDialog(true); };

    const submit = async () => {
        if (!form.departemen_id) return toast.error("Pilih Departemen induk");
        try {
            if (editing) await api.put(`/divisi-klinik/${editing.id}`, form);
            else await api.post("/divisi-klinik", form);
            toast.success(editing ? "Divisi diperbarui" : "Divisi ditambahkan");
            setDialog(false); reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const del = async () => {
        try {
            await api.delete(`/divisi-klinik/${confirmDel.id}`);
            toast.success("Divisi dihapus");
            setConfirmDel(null); reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const filtered = divisi.filter((d) => filterDep === "all" || d.departemen_id === filterDep);

    return (
        <>
            <div className="flex flex-wrap justify-between gap-2 mb-3">
                <Select value={filterDep} onValueChange={setFilterDep}>
                    <SelectTrigger className="w-64"><SelectValue placeholder="Filter Departemen" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Semua Departemen</SelectItem>
                        {departemen.map((d) => <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button size="sm" onClick={openAdd} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="divisi-add-button">
                    <Plus className="w-4 h-4 mr-1.5" /> Tambah Divisi
                </Button>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full compact">
                    <thead className="bg-slate-100">
                        <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                            <th>Nama Divisi</th><th>Departemen</th><th className="text-right pr-4">Aksi</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((d) => (
                            <tr key={d.id} className="border-t border-slate-200 hover:bg-slate-50">
                                <td className="font-semibold text-slate-800">{d.nama}</td>
                                <td className="text-slate-600">{departemenMap[d.departemen_id]?.nama || "-"}</td>
                                <td className="text-right pr-2">
                                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)}><Pencil className="w-4 h-4" /></Button>
                                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setConfirmDel(d)}><Trash2 className="w-4 h-4" /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? "Edit Divisi" : "Tambah Divisi"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Departemen Induk</Label>
                            <Select value={form.departemen_id} onValueChange={(v) => setForm({ ...form, departemen_id: v })}>
                                <SelectTrigger data-testid="divisi-form-departemen"><SelectValue placeholder="Pilih departemen" /></SelectTrigger>
                                <SelectContent>
                                    {departemen.map((d) => <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Nama Divisi</Label>
                            <Input data-testid="divisi-form-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Batal</Button>
                        <Button onClick={submit} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="divisi-form-submit">Simpan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus divisi?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmDel?.nama} akan dihapus. Divisi yang masih dipakai di data Rotasi Klinik tidak bisa dihapus.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={del}>Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

function InstalasiPanel({ instalasi, reload }) {
    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [nama, setNama] = useState("");
    const [confirmDel, setConfirmDel] = useState(null);

    const openAdd = () => { setEditing(null); setNama(""); setDialog(true); };
    const openEdit = (d) => { setEditing(d); setNama(d.nama); setDialog(true); };

    const submit = async () => {
        try {
            if (editing) await api.put(`/instalasi-klinik/${editing.id}`, { nama });
            else await api.post("/instalasi-klinik", { nama });
            toast.success(editing ? "Instalasi diperbarui" : "Instalasi ditambahkan");
            setDialog(false); reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const del = async () => {
        try {
            await api.delete(`/instalasi-klinik/${confirmDel.id}`);
            toast.success("Instalasi dihapus");
            setConfirmDel(null); reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <>
            <div className="flex justify-end mb-3">
                <Button size="sm" onClick={openAdd} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="instalasi-add-button">
                    <Plus className="w-4 h-4 mr-1.5" /> Tambah Instalasi
                </Button>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full compact">
                    <thead className="bg-slate-100">
                        <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                            <th>Nama Instalasi</th><th className="text-right pr-4">Aksi</th>
                        </tr>
                    </thead>
                    <tbody>
                        {instalasi.map((d) => (
                            <tr key={d.id} className="border-t border-slate-200 hover:bg-slate-50">
                                <td className="font-semibold text-slate-800">{d.nama}</td>
                                <td className="text-right pr-2">
                                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)}><Pencil className="w-4 h-4" /></Button>
                                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setConfirmDel(d)}><Trash2 className="w-4 h-4" /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? "Edit Instalasi" : "Tambah Instalasi"}</DialogTitle></DialogHeader>
                    <div>
                        <Label className="text-xs font-semibold uppercase text-slate-600">Nama Instalasi</Label>
                        <Input data-testid="instalasi-form-nama" value={nama} onChange={(e) => setNama(e.target.value)} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Batal</Button>
                        <Button onClick={submit} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="instalasi-form-submit">Simpan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus instalasi?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmDel?.nama} akan dihapus. Instalasi yang masih dipakai di data Rotasi Klinik tidak bisa dihapus.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={del}>Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
